<template>
  <v-card class="creator">
    <v-card-title>
      <h2>{{ $t('Store Creator') }}</h2>
    </v-card-title>

    <v-divider/>

    <v-card-text>
      <v-expansion-panel
        v-model="panels"
      >
        <v-expansion-panel-content>
          <template v-slot:header>
            <v-flex>{{ $t('Base Info') }}</v-flex>
          </template>
          <template v-slot:actions>
            <v-icon
              v-if="!isDataFilled"
              color="error"
            >
              error
            </v-icon>
            <v-icon
              v-else
              color="teal"
            >
              done
            </v-icon>
          </template>

          <v-divider/>

          <v-flex class="pa-4">
            <store-form
              :store="storeData"
              :is-value-immutable="false"
              hide-code
            />
          </v-flex>
        </v-expansion-panel-content>

        <v-expansion-panel-content>
          <template v-slot:header>
            <v-flex>{{ $t('Currency and Locale') }}</v-flex>
          </template>
          <template v-slot:actions>
            <v-icon
              v-if="!isMiscFilled"
              color="error"
            >
              error
            </v-icon>
            <v-icon
              v-else
              color="teal"
            >
              done
            </v-icon>
          </template>

          <v-divider/>

          <v-flex class="pa-4">
            <v-combobox
              v-model="currency"
              :items="currencies"
              :label="$t('Store Currency')"
            />
            <v-text-field
              v-model.trim="locale"
              :error-messages="localeValidation"
              :label="$t('Store Locale')"
            />
          </v-flex>
        </v-expansion-panel-content>

        <v-expansion-panel-content>
          <template v-slot:header>
            <v-flex>{{ $t('Language') }}</v-flex>
          </template>
          <template v-slot:actions>
            <v-icon
              v-if="!isLanguageFilled"
              color="error"
            >
              error
            </v-icon>
            <v-icon
              v-else
              color="teal"
            >
              done
            </v-icon>
          </template>

          <v-divider/>

          <v-flex class="pa-4">
            <language-form v-model="language"/>
          </v-flex>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-card-text>

    <v-divider/>

    <v-card-actions>
      <v-btn
        v-if="!canCreate()"
        :disabled="panels === 0"
        color="primary"
        @click="previous()"
      >
        {{ $t('Previous') }}
      </v-btn>

      <v-spacer/>
      <v-btn
        v-if="!canCreate()"
        :disabled="panels === 2"
        color="primary"
        @click="next()"
      >
        {{ $t('Next') }}
      </v-btn>
      <v-btn
        v-else
        color="primary"
        @click="create()"
      >
        {{ $t('Create') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import StoreForm from '@/modules/Settings/components/StoreForm'
import currencies from 'js-money/lib/currency'
import LanguageForm from '@/modules/Settings/components/Language/LanguageForm'
import { mapActions } from 'vuex'

export default {
  name: 'StoreCreator',
  components: { LanguageForm, StoreForm },
  data () {
    return {
      panels: 0,
      currencies: [],
      storeData: {
        name: '',
        color: '',
        url: '',
        template: 'default'
      },
      currency: '',
      locale: '',
      language: {
        name: '',
        code: '',
        iso: '',
        flag: '',
        dir: ''
      }
    }
  },

  computed: {
    localeValidation () {
      if (!this.locale) {
        return ''
      }

      const valid = this.locale.match('^[a-z]{2}-[A-Z]{2}$')
      return valid ? '' : this.$t('Invalid locale format. Valid format: `xx-XX`')
    },

    isDataFilled () {
      return Object.values(this.storeData).every(val => val !== '')
    },

    isLanguageFilled () {
      return Object.values(this.language).every(val => val !== '')
    },

    isMiscFilled () {
      return this.currency && this.locale
    }
  },

  created () {
    this.currencies = Object.keys(currencies)
  },

  methods: {
    ...mapActions(['addStore']),

    canCreate () {
      return this.isDataFilled && this.isLanguageFilled && this.isMiscFilled
    },

    previous () {
      this.panels = this.panels === 0 ? 2 : this.panels - 1
    },

    next () {
      this.panels = this.panels === 2 ? 0 : this.panels + 1
    },

    create () {
      const data = this.storeData
      data['color'] = this.storeData.color.hex
      data['currency'] = this.currency
      data['locale'] = this.locale
      data['language'] = this.language

      const store = this.$http.make('stores', data)

      store.save().then(response => {
        this.$emit('created')
        this.addStore(response.payload())

        this.clear()

        this.$toast.success(this.$t('Store created successfully.'))
        this.$toast.info(this.$t('Store creation processes may take a while and some things may appear with a delay.'))
      }).catch(e => {
        this.$toast.error(e.message)
      })
    },

    clear () {
      this.panels = 0
      this.storeData = {
        name: '',
        color: '',
        url: '',
        template: 'default'
      }
      this.currency = ''
      this.locale = ''
      this.language = {
        name: '',
        code: '',
        iso: '',
        flag: '',
        dir: ''
      }
    }
  }
}
</script>
